import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import {
  Palette, Globe, CheckCircle2, Eye, ArrowRight, Shield,
  RefreshCw, Upload, Type
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

export default function WhiteLabelSettings() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasConfig, setHasConfig] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [configRes, pkgRes] = await Promise.all([
          api.get('/api/whitelabel/config'),
          api.get('/api/whitelabel/packages'),
        ]);
        setConfig(configRes.data.config || {});
        setHasConfig(configRes.data.has_config);
        setPackages(pkgRes.data.packages || []);
      } catch (e) { console.warn('[whitelabel] fetch failed', e); } finally { setLoading(false); }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/api/whitelabel/config', config);
      setConfig(data.config);
      setHasConfig(true);
      toast.success('White-label config saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleCheckout = async (packageId) => {
    try {
      const { data } = await api.post('/api/whitelabel/checkout', {
        package_id: packageId, origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
    } catch { toast.error('Checkout failed'); }
  };

  const updateField = (field, value) => setConfig(prev => ({ ...prev, [field]: value }));

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab="White Label" onTabChange={() => {}} />
      <main className="flex-1 flex items-center justify-center"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="whitelabel-page">
      <Sidebar activeTab="White Label" onTabChange={() => {}} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="max-w-4xl space-y-6 animate-fade-in">
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">White-Label Settings</h1>
            <p className="text-sm text-gray-500 mt-1">Customize the platform with your own branding for your team and clients</p>
          </div>

          {/* Branding Config */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-5" data-testid="branding-config">
            <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2">
              <Palette className="w-4 h-4 text-[#D4A843]" /> Branding
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Company Name</Label>
                <Input value={config?.company_name || ''} onChange={e => updateField('company_name', e.target.value)} className="mt-1 rounded-lg" data-testid="wl-company-name" />
              </div>
              <div>
                <Label className="text-xs">Support Email</Label>
                <Input value={config?.support_email || ''} onChange={e => updateField('support_email', e.target.value)} className="mt-1 rounded-lg" placeholder="support@yourco.com" data-testid="wl-support-email" />
              </div>
              <div>
                <Label className="text-xs">Logo URL</Label>
                <Input value={config?.logo_url || ''} onChange={e => updateField('logo_url', e.target.value)} className="mt-1 rounded-lg" placeholder="https://..." data-testid="wl-logo" />
              </div>
              <div>
                <Label className="text-xs">Favicon URL</Label>
                <Input value={config?.favicon_url || ''} onChange={e => updateField('favicon_url', e.target.value)} className="mt-1 rounded-lg" placeholder="https://..." data-testid="wl-favicon" />
              </div>
            </div>

            <h3 className="text-xs font-semibold text-gray-700 mt-4 flex items-center gap-1.5">
              <Type className="w-3 h-3" /> Colors & Fonts
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Primary Color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={config?.primary_color || '#D4A843'} onChange={e => updateField('primary_color', e.target.value)} className="w-10 h-10 rounded border cursor-pointer" data-testid="wl-primary-color" />
                  <Input value={config?.primary_color || ''} onChange={e => updateField('primary_color', e.target.value)} className="rounded-lg flex-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Secondary Color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={config?.secondary_color || '#0F172A'} onChange={e => updateField('secondary_color', e.target.value)} className="w-10 h-10 rounded border cursor-pointer" data-testid="wl-secondary-color" />
                  <Input value={config?.secondary_color || ''} onChange={e => updateField('secondary_color', e.target.value)} className="rounded-lg flex-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Accent Color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={config?.accent_color || '#10B981'} onChange={e => updateField('accent_color', e.target.value)} className="w-10 h-10 rounded border cursor-pointer" data-testid="wl-accent-color" />
                  <Input value={config?.accent_color || ''} onChange={e => updateField('accent_color', e.target.value)} className="rounded-lg flex-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Heading Font</Label>
                <Input value={config?.font_heading || ''} onChange={e => updateField('font_heading', e.target.value)} className="mt-1 rounded-lg" data-testid="wl-font-heading" />
              </div>
              <div>
                <Label className="text-xs">Body Font</Label>
                <Input value={config?.font_body || ''} onChange={e => updateField('font_body', e.target.value)} className="mt-1 rounded-lg" data-testid="wl-font-body" />
              </div>
            </div>

            <h3 className="text-xs font-semibold text-gray-700 mt-4 flex items-center gap-1.5">
              <Globe className="w-3 h-3" /> Advanced
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Custom Domain (CNAME)</Label>
                <Input value={config?.custom_domain || ''} onChange={e => updateField('custom_domain', e.target.value)} className="mt-1 rounded-lg" placeholder="app.yourcompany.com" data-testid="wl-domain" />
              </div>
              <div>
                <Label className="text-xs">Login Background Image URL</Label>
                <Input value={config?.login_background_url || ''} onChange={e => updateField('login_background_url', e.target.value)} className="mt-1 rounded-lg" data-testid="wl-login-bg" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Dashboard Welcome Message</Label>
                <Input value={config?.dashboard_welcome_text || ''} onChange={e => updateField('dashboard_welcome_text', e.target.value)} className="mt-1 rounded-lg" placeholder="Welcome to our IT portal" data-testid="wl-welcome" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Custom CSS</Label>
                <Textarea value={config?.custom_css || ''} onChange={e => updateField('custom_css', e.target.value)} className="mt-1 rounded-lg font-mono text-xs" rows={4} placeholder=".sidebar-dark { background: #1a1a2e; }" data-testid="wl-css" />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={config?.hide_powered_by || false} onChange={e => updateField('hide_powered_by', e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-[#D4A843]" data-testid="wl-hide-badge" />
                <span className="text-sm text-gray-700">Hide "Powered by TechOps" badge</span>
              </label>
            </div>

            <Button onClick={handleSave} disabled={saving} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="save-wl-btn">
              {saving ? 'Saving...' : 'Save Branding'}
            </Button>
          </div>

          {/* Live Preview */}
          {config?.company_name && (
            <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="wl-preview">
              <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Eye className="w-4 h-4 text-[#D4A843]" /> Live Preview
              </h2>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div style={{ background: config.secondary_color || '#0F172A' }} className="px-5 py-3 flex items-center gap-2.5">
                  {config.logo_url ? (
                    <img src={config.logo_url} alt="" className="h-7 w-7 rounded object-cover" />
                  ) : (
                    <div style={{ background: config.primary_color || '#D4A843' }} className="w-7 h-7 rounded-lg flex items-center justify-center">
                      <Shield className="w-3.5 h-3.5 text-white" />
                    </div>
                  )}
                  <span className="text-sm font-bold text-white" style={{ fontFamily: config.font_heading || 'Cabinet Grotesk' }}>
                    {config.company_name || 'TechOps'}
                  </span>
                </div>
                <div className="p-5 bg-gray-50">
                  <p className="text-sm text-gray-600" style={{ fontFamily: config.font_body || 'IBM Plex Sans' }}>
                    {config.dashboard_welcome_text || `Welcome to ${config.company_name || 'TechOps'}`}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <button style={{ background: config.primary_color || '#D4A843' }} className="text-white text-xs px-4 py-2 rounded-lg font-medium">Primary Button</button>
                    <button style={{ background: config.accent_color || '#10B981' }} className="text-white text-xs px-4 py-2 rounded-lg font-medium">Accent Button</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Deployment Packages */}
          <div>
            <h2 className="font-heading text-lg font-bold text-gray-900 mb-4">Deployment Packages</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="wl-packages">
              {packages.map((pkg, i) => (
                <div key={pkg.id} className={`bg-white border rounded-lg p-6 ${i === 1 ? 'border-[#D4A843] shadow-sm' : 'border-gray-200'}`} data-testid={`wl-pkg-${pkg.id}`}>
                  {i === 1 && <span className="inline-block text-[10px] font-bold uppercase tracking-wider bg-[#D4A843] text-[#0A0A0A] font-semibold px-2 py-0.5 rounded mb-3">Most Popular</span>}
                  <h3 className="text-sm font-bold text-gray-900">{pkg.name}</h3>
                  <div className="flex items-baseline gap-1 mt-2 mb-4">
                    <span className="font-heading text-3xl font-bold text-gray-900">${pkg.price}</span>
                    <span className="text-sm text-gray-400">{pkg.period}</span>
                  </div>
                  <ul className="space-y-2 mb-5">{pkg.features.map((f, j) => (
                    <li key={`j-${j}`} className="flex items-start gap-2 text-xs text-gray-600">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" strokeWidth={2} />{f}
                    </li>
                  ))}</ul>
                  <Button className={`w-full rounded-lg text-sm ${i === 1 ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'bg-white text-gray-900 border border-gray-300'}`}
                    onClick={() => handleCheckout(pkg.id)} data-testid={`wl-checkout-${pkg.id}`}
                  >Get Started <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
